import { chartAreaGradient } from "../../../charts/ChartjsConfig";
import { processChartData } from "./charts/ChartDataProcessor";
import LineChartAnswers from "./charts/LineChartAnswers";

// Import utilities
import { LoadingSpinner } from "@shared/components/LoadingSpinner";
import theme from "@shared/utils/themeColors";
import { hexToRGB, tailwindConfig } from "../../../utils/Utils";
import { useAnalyticsData } from "../hooks/useAnalyticsData";

function MathCard() {
  const { loading, error, analyticsData, sectionStats } = useAnalyticsData();

  if (loading) {
    return (
      <div
        className={`flex flex-col col-span-full sm:col-span-6 xl:col-span-4 ${theme.bg.primary} shadow-sm rounded-xl p-5`}
      >
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col col-span-full sm:col-span-6 xl:col-span-4 ${theme.bg.primary} shadow-sm rounded-xl p-5`}
      >
        <p className={`text-center ${theme.text.error}`}>{error}</p>
      </div>
    );
  }

  if (!analyticsData || !sectionStats) {
    return (
      <div
        className={`flex flex-col col-span-full sm:col-span-6 xl:col-span-4 ${theme.bg.primary} shadow-sm rounded-xl p-5`}
      >
        <p className={`text-center ${theme.text.muted}`}>
          No math performance data available
        </p>
      </div>
    );
  }

  const { sections: chartData } = analyticsData;

  // Format the chart data
  const mathChartData = {
    labels: chartData.math.labels,
    datasets: [
      // Green line (correct answers)
      {
        data: chartData.math.datasets.correctAnswers,
        fill: true,
        backgroundColor: function (context) {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          return chartAreaGradient(ctx, chartArea, [
            {
              stop: 0,
              color: `rgba(${hexToRGB(
                tailwindConfig().theme.colors.green[500]
              )}, 0)`,
            },
            {
              stop: 1,
              color: `rgba(${hexToRGB(
                tailwindConfig().theme.colors.green[500]
              )}, 0.2)`,
            },
          ]);
        },
        borderColor: tailwindConfig().theme.colors.green[500],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointBackgroundColor: tailwindConfig().theme.colors.green[500],
        pointHoverBackgroundColor: tailwindConfig().theme.colors.green[500],
        pointBorderWidth: 0,
        pointHoverBorderWidth: 0,
        clip: 20,
        tension: 0.2,
      },
      // Gray line (incorrect answers)
      {
        data: chartData.math.datasets.incorrectAnswers,
        borderColor: `rgba(${hexToRGB(
          tailwindConfig().theme.colors.gray[500]
        )}, 0.25)`,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointBackgroundColor: `rgba(${hexToRGB(
          tailwindConfig().theme.colors.gray[500]
        )}, 0.25)`,
        pointHoverBackgroundColor: `rgba(${hexToRGB(
          tailwindConfig().theme.colors.gray[500]
        )}, 0.25)`,
        pointBorderWidth: 0,
        pointHoverBorderWidth: 0,
        clip: 20,
        tension: 0.2,
      },
    ],
  };

  return (
    <div
      className={`flex flex-col col-span-full sm:col-span-6 xl:col-span-4 ${theme.bg.primary} shadow-sm rounded-xl`}
    >
      <div className="px-5 pt-5">
        <header className="flex justify-between items-start mb-2">
          <h2 className={`text-lg font-semibold ${theme.text.primary} mb-2`}>
            Math
          </h2>
        </header>
        <div
          className={`text-xs font-semibold uppercase mb-1 ${theme.text.inactive}`}
        >
          Correct Answers
        </div>
        <div className="flex items-start">
          <div className={`text-3xl font-bold ${theme.text.primary} mr-2`}>
            {sectionStats.math.totalCorrect}
          </div>
          <div className={`text-sm font-bold ${theme.text.primary} mr-2`}>
            / {sectionStats.math.totalQuestions}
          </div>
          <div
            className={`text-sm font-medium ${theme.text.successDark} px-1.5 ${theme.bg.success}/20 rounded-full`}
          >
            {sectionStats.math.correctPercentage}%
          </div>
        </div>
      </div>
      {/* Chart built with Chart.js 3 */}
      <div className="grow max-sm:max-h-[128px] xl:max-h-[128px]">
        {/* Change the height attribute to adjust the chart height */}
        <LineChartAnswers
          data={processChartData(mathChartData)}
          width={389}
          height={128}
          useTimeScale={false}
        />
      </div>
    </div>
  );
}

export default MathCard;
